import { AppTextService } from '@fi-shared/app-text';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { Injectable } from '@angular/core';
import { TextEditorHistoryService } from '../fi-text-editor-helper-services/text-editor-history.service';
import { LinkToTypesService } from '@fi-shared/link-to/link-to-types.service';
import { TextEditorInsertLinkDialogComponent } from './text-editor-insert-link-dialog.component';
import { findClosest } from '@fi-shared/utils';
import {
    IInsertLinkConfig,
    IInsertLinkContentComponentConfig,
    IInsertNewElementConfig,
    ILinkElementConfig,
    ILinkElementTextConfig,
    ITextEditorInsertLinkDialogData,
} from '@fi-shared/fi-text-editor/fi-text-editor-insert-link/text-editor-insert-link';
import { FiLinkToType } from '@fi-shared/fi-text-item-helper/fi-link-to-type';
import { ITextItemLinkModel } from '@fi-shared/fi-text-item-helper/fi-text-item-link';

@Injectable()
export class TextEditorInsertLinkService {
    isEditLinkMode: boolean;

    constructor(
        private textEditorHistoryService: TextEditorHistoryService,
        private linkToTypes: LinkToTypesService,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
    ) {
        /*setTimeout(() => { // for insert link tests
            return this.fiDialogsService.custom({
                yesButtonText: this.appTextService.get('done'),
                headerText: this.appTextService.get('text_editor.insert_link'),
                contentComponentData: {
                    model: {
                        Type: FiLinkToType.ChecklistV2
                    }
                },
                contentComponentRef: TextEditorInsertLinkDialogComponent,
                config: {
                    panelClass: 'insert-link-operation-overlay-small',
                    minWidth: '360px'
                }
            })
        }, 500)*/
    }

    insertLink(insertLinkConfig: IInsertLinkConfig) {
        insertLinkConfig.params = insertLinkConfig.params || {};
        const selection = window.getSelection();
        this.isEditLinkMode = false;
        if (!insertLinkConfig.range) {
            insertLinkConfig.range = this.getDefaultRange(selection, insertLinkConfig.parentElement);
        }
        const linkItem: HTMLElement = this.getLinkItem(insertLinkConfig.range);
        insertLinkConfig.params = this.getInsertLinkContentComponentData({
            range: insertLinkConfig.range,
            params: insertLinkConfig.params,
            linkItem,
            linkToType: insertLinkConfig.linkToType,
        });

        return this.fiDialogsService
            .custom({
                yesButtonText: this.appTextService.get('done'),
                headerText: this.appTextService.get('text_editor.insert_link'),
                contentComponentData: insertLinkConfig.params,
                contentComponentRef: TextEditorInsertLinkDialogComponent,
                hideDialogTopBorder: true,
                isHeaderDividerShown: true,
                config: {
                    panelClass: ['fi-rounded-dialog-panel', 'fi-insert-link-dialog-panel'],
                    minWidth: '360px',
                    autoFocus: false,
                },
            })
            .onYesButtonClick((linkToModel) => {
                this.textEditorHistoryService.saveState();
                const insertElement: HTMLAnchorElement = this.getInsertLinkElement({
                    linkToModel,
                    title: insertLinkConfig.params.title,
                });
                this.insertNewElement({
                    insertElement,
                    linkItem,
                    range: insertLinkConfig.range,
                    selection,
                });
                this.triggerEditorChangeEvent(insertElement);
            });
    }

    private getDefaultRange(selection: Selection, parentElement: HTMLElement): Range {
        if (selection.rangeCount) {
            const range = selection.getRangeAt(0);
            if (!this.isElementAncestorsTextEditor(range.commonAncestorContainer as HTMLElement)) return range;
        }
        return this.includeParentEditorInRange(parentElement);
    }

    private isElementAncestorsTextEditor(element: HTMLElement): boolean {
        return !findClosest(element, '.text-editor') && !findClosest(element, '.fi-contenteditable');
    }

    private getTextItemLinkElem(linkItem: HTMLElement): HTMLElement {
        return linkItem.parentElement.matches('ccm-text-item-link') ? linkItem.parentElement : linkItem;
    }

    private includeParentEditorInRange(parentElement: HTMLElement): Range {
        const range = document.createRange();
        const contentEditableElement: Element = parentElement.matches('[contenteditable]')
            ? parentElement
            : parentElement.querySelector('[contenteditable]');
        if (contentEditableElement) {
            range.selectNodeContents(contentEditableElement);
            range.collapse(true);
        }
        return range;
    }

    private getLinkItem(range: Range): HTMLElement {
        if (range.endContainer instanceof HTMLElement) return range.endContainer;

        if (range) return range.endContainer.parentNode as HTMLElement;
    }

    private getInsertLinkContentComponentData(
        config: IInsertLinkContentComponentConfig,
    ): ITextEditorInsertLinkDialogData {
        let selectedText = config.range.startContainer.textContent;

        if (config.range.startOffset !== selectedText?.length) {
            config.params.title = selectedText.slice(config.range.startOffset, config.range.endOffset);
        }

        if (config.linkItem.tagName.toLowerCase() === 'a') {
            const textItemLinkElem = this.getTextItemLinkElem(config.linkItem);
            const textItemModelString = textItemLinkElem.getAttribute('text-item-model');
            if (textItemModelString) {
                // linkItem can be ccm-text-item-media-model
                config.params.model = JSON.parse(textItemModelString);
                config.params.title = config.linkItem.innerText;
                this.isEditLinkMode = true;
            }
        }

        if (!config.params.model) {
            config.params.model = {};
        }

        if (!config.params.model.Type) {
            config.params.model.Type = config.linkToType;
        }

        return config.params;
    }

    private getInsertLinkElement(linkElementConfig: ILinkElementConfig): HTMLAnchorElement {
        const textModel: ITextItemLinkModel = {
            Type: linkElementConfig.linkToModel.Type,
            Value: linkElementConfig.linkToModel.Value,
        };

        // keep href="" until item get inserted because of issue
        // FI-369 Links to websites in pages is working incorrectly
        const insertLinkElement: HTMLAnchorElement = document.createElement('a');
        insertLinkElement.setAttribute('href', '');
        insertLinkElement.setAttribute('ccm-text-item-link', '');
        insertLinkElement.setAttribute('text-item-model', JSON.stringify(textModel));
        insertLinkElement.innerText = this.getInsertLinkElementInnerText({
            linkToType: textModel.Type,
            modelTitle: linkElementConfig.linkToModel.EditTitle,
            title: linkElementConfig.title,
            modelValue: this.getTextModelValue(textModel),
        });
        return insertLinkElement;
    }

    private getTextModelValue(textModel: ITextItemLinkModel): string {
        return textModel.Value.Name;
    }

    private getInsertLinkElementInnerText(linkElementTextConfig: ILinkElementTextConfig): string {
        if (linkElementTextConfig.linkToType === FiLinkToType.WebAddress)
            return linkElementTextConfig.modelTitle || linkElementTextConfig.modelValue;

        if (
            linkElementTextConfig.linkToType === FiLinkToType.Email ||
            linkElementTextConfig.linkToType === FiLinkToType.PhoneNumber
        )
            return linkElementTextConfig.modelValue;

        return linkElementTextConfig.modelTitle || linkElementTextConfig.title || linkElementTextConfig.modelValue;
    }

    private insertNewElement(insertElementConfig: IInsertNewElementConfig) {
        if (this.isEditLinkMode) {
            // due to changes in logic, this element can be A or CCM-TEXT-ITEM-LINK (old version)
            this.getTextItemLinkElem(insertElementConfig.linkItem).replaceWith(insertElementConfig.insertElement);
        } else {
            this.insertNewElementInRange(insertElementConfig);
        }
    }

    private insertNewElementInRange(insertElementConfig: IInsertNewElementConfig) {
        insertElementConfig.range.deleteContents();
        insertElementConfig.range.insertNode(insertElementConfig.insertElement);
        insertElementConfig.range.setStartAfter(insertElementConfig.insertElement);
        insertElementConfig.range.setEndAfter(insertElementConfig.insertElement);
        insertElementConfig.range.collapse(false);
        insertElementConfig.selection.removeAllRanges();
        insertElementConfig.selection.addRange(insertElementConfig.range);
    }

    private triggerEditorChangeEvent(insertElement: HTMLAnchorElement) {
        const editorEl: HTMLElement = findClosest(insertElement, '.text-editor');
        if (editorEl) {
            editorEl.dispatchEvent(new Event('change'));
        }
    }
}
